import * as React from 'react';

import { cn } from '@/lib/utils';

const TabsContext = React.createContext(null);

function Tabs({ value: controlled, defaultValue, onValueChange, className, children }) {
	const [internal, setInternal] = React.useState(defaultValue);
	const value = controlled ?? internal;
	const setValue = React.useCallback(
		(next) => {
			if (controlled === undefined) setInternal(next);
			onValueChange?.(next);
		},
		[controlled, onValueChange]
	);
	return (
		<TabsContext.Provider value={{ value, setValue }}>
			<div className={className}>{children}</div>
		</TabsContext.Provider>
	);
}

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
	<div ref={ref} role="tablist" className={cn('inline-flex h-10 items-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground', className)} {...props} />
));
TabsList.displayName = 'TabsList';

const TabsTrigger = React.forwardRef(({ className, value, ...props }, ref) => {
	const ctx = React.useContext(TabsContext);
	const active = ctx?.value === value;
	return (
		<button
			ref={ref}
			role="tab"
			aria-selected={active}
			onClick={() => ctx?.setValue(value)}
			className={cn(
				'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all',
				'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
				active ? 'bg-background text-foreground shadow-soft' : 'hover:text-foreground',
				className
			)}
			{...props}
		/>
	);
});
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef(({ className, value, ...props }, ref) => {
	const ctx = React.useContext(TabsContext);
	if (ctx?.value !== value) return null;
	return <div ref={ref} role="tabpanel" className={cn('mt-3 focus-visible:outline-none', className)} {...props} />;
});
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };
